import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Crosshair, Search } from "lucide-react";
import { geoAutosuggest } from "../lib/api";

const TOKEN = process.env.REACT_APP_MAPPLS_TOKEN;
const BASE = process.env.REACT_APP_BACKEND_URL;

// Mappls SDK is loaded INSIDE a sandboxed iframe so any document.write it does
// cannot wipe the host app. The iframe handles map + click-to-pin only; the
// autocomplete lives in the parent (backend OAuth proxy) for full control.
function iframeDoc(token) {
  return `<!doctype html><html><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>html,body,#map{height:100%;width:100%;margin:0}</style>
<script src="https://apis.mappls.com/advancedmaps/api/${token}/map_sdk?layer=vector&v=3.0&callback=initMap"></script>
</head><body><div id="map"></div>
<script>
var map, marker;
function place(lat,lng){
  if(marker){try{marker.remove();}catch(e){}}
  try{ marker = new mappls.Marker({map:map, position:{lat:lat,lng:lng}, draggable:true, fitbounds:false});
    if(marker.addListener){ marker.addListener('dragend', function(){ var p=marker.getPosition?marker.getPosition():null; if(p){ parent.postMessage({type:'mappls-pick',lat:p.lat,lng:p.lng},'*'); } }); }
  }catch(e){}
}
function initMap(){
  try{
    map = new mappls.Map('map',{center:[12.9716,77.5946],zoom:11,zoomControl:true});
    map.on('load', function(){ parent.postMessage({type:'mappls-ready'},'*'); });
    map.on('click', function(e){ var lat=e.lngLat?e.lngLat.lat:e.lat, lng=e.lngLat?e.lngLat.lng:e.lng; place(lat,lng); parent.postMessage({type:'mappls-pick',lat:lat,lng:lng},'*'); });
    setTimeout(function(){ parent.postMessage({type:'mappls-ready'},'*'); }, 2000);
  }catch(e){ parent.postMessage({type:'mappls-fail'},'*'); }
}
window.addEventListener('message', function(ev){
  var d=ev.data||{};
  if(d.type==='mappls-goto' && map){ try{ map.setView?map.setView([d.lat,d.lng],15):map.flyTo({center:[d.lat,d.lng],zoom:15}); }catch(e){} place(d.lat,d.lng); }
});
window.onerror=function(){ parent.postMessage({type:'mappls-fail'},'*'); };
</script></body></html>`;
}

export default function LocationPicker({ onChange }) {
  const iframeRef = useRef(null);
  const boxRef = useRef(null);
  const [status, setStatus] = useState("checking"); // checking | ready | error
  const [coords, setCoords] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  const reverse = async (lat, lng) => {
    setResolving(true);
    try {
      const r = await fetch(`${BASE}/api/geo/reverse?lat=${lat}&lng=${lng}`);
      const d = r.ok ? await r.json() : null;
      onChange?.({ lat, lng, address: d?.formatted_address || "" });
    } catch {
      onChange?.({ lat, lng, address: "" });
    } finally { setResolving(false); }
  };

  // verify token before mounting the SDK iframe
  useEffect(() => {
    if (!TOKEN) { setStatus("error"); return; }
    let cancelled = false;
    fetch(`${BASE}/api/geo/status`)
      .then((r) => (r.ok ? r.json() : { ok: false }))
      .then((s) => { if (!cancelled) setStatus(s.ok ? "ready" : "error"); })
      .catch(() => { if (!cancelled) setStatus("error"); });
    return () => { cancelled = true; };
  }, []);

  // listen for picks from the sandboxed iframe
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev.data || {};
      if (d.type === "mappls-pick" && Number.isFinite(d.lat) && Number.isFinite(d.lng)) {
        setCoords({ lat: d.lat, lng: d.lng });
        reverse(d.lat, d.lng);
      } else if (d.type === "mappls-fail") {
        setStatus("error");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // debounced autocomplete
  useEffect(() => {
    if (q.trim().length < 2) { setSuggestions([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try { const s = await geoAutosuggest(q.trim()); setSuggestions(s); setOpen(true); }
      catch { setSuggestions([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  // close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const chooseSuggestion = (s) => {
    const full = [s.name, s.address].filter(Boolean).join(", ");
    setQ(s.name || "");
    setOpen(false);
    setSuggestions([]);
    onChange?.({ address: full });
  };

  const locateMe = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const { latitude, longitude } = pos.coords;
      setCoords({ lat: latitude, lng: longitude });
      iframeRef.current?.contentWindow?.postMessage({ type: "mappls-goto", lat: latitude, lng: longitude }, "*");
      reverse(latitude, longitude);
    });
  };

  if (status === "error") {
    return (
      <div data-testid="map-fallback" className="mt-2 flex items-center gap-2 rounded-xl border border-[#E4D8C7] bg-[#F5EFE4] px-3 py-2.5 text-xs text-[#7A6E64]">
        <MapPin size={14} className="text-[#8B1A2B]" /> Map is unavailable right now — just type your address below.
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid="location-picker">
      {/* autocomplete */}
      <div ref={boxRef} className="relative">
        <div className="flex items-center gap-2 rounded-xl border border-[#E4D8C7] bg-white px-3">
          <Search size={15} className="shrink-0 text-[#8B1A2B]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => suggestions.length && setOpen(true)}
            placeholder="Search area, locality or PIN code…"
            data-testid="map-search-input"
            className="w-full bg-transparent py-3 text-sm outline-none"
          />
          {searching && <Loader2 size={14} className="animate-spin text-[#A0968A]" />}
        </div>
        {open && suggestions.length > 0 && (
          <ul data-testid="map-suggestions" className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-[#E4D8C7] bg-white py-1 shadow-lg">
            {suggestions.map((s, i) => (
              <li key={s.eloc || i}>
                <button type="button" onClick={() => chooseSuggestion(s)} data-testid={`map-suggestion-${i}`}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-[#F7F1E7]">
                  <MapPin size={14} className="mt-0.5 shrink-0 text-[#8B1A2B]" />
                  <span>
                    <span className="block text-sm font-medium text-[#2A211C]">{s.name}</span>
                    {s.address && <span className="block text-xs text-[#7A6E64]">{s.address}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* map */}
      <div className="relative mt-2 overflow-hidden rounded-2xl border border-[#E4D8C7]">
        {status === "checking" ? (
          <div className="flex h-[220px] items-center justify-center bg-[#F5EFE4] text-xs text-[#7A6E64]">
            <Loader2 size={16} className="mr-2 animate-spin" /> Loading map…
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            title="Pick your ceremony location"
            data-testid="map-iframe"
            sandbox="allow-scripts allow-same-origin"
            srcDoc={iframeDoc(TOKEN)}
            style={{ width: "100%", height: "220px", border: 0, display: "block" }}
          />
        )}
        {status === "ready" && (
          <button type="button" onClick={locateMe} data-testid="map-locate-btn"
            className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[#8B1A2B] shadow-md">
            <Crosshair size={13} /> Use my location
          </button>
        )}
      </div>
      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[#7A6E64]" data-testid="map-hint">
        {resolving ? <><Loader2 size={12} className="animate-spin" /> Finding address…</>
          : coords ? <><MapPin size={12} className="text-[#3E6B4F]" /> Pinned {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)} — address filled below</>
          : "Search above, or tap the map to drop a pin at the exact spot."}
      </p>
    </div>
  );
}
